import { useEffect, useCallback } from 'react';

interface KeyboardShortcut {
  key: string;
  metaKey?: boolean;
  ctrlKey?: boolean;
  shiftKey?: boolean;
  altKey?: boolean;
  callback: () => void | Promise<void>;
  description: string;
}

/**
 * Hook for managing global keyboard shortcuts
 * Supports Cmd/Ctrl+number shortcuts for tab navigation
 */
export function useKeyboardShortcuts(shortcuts: KeyboardShortcut[]) {
  const handleKeyDown = useCallback((event: KeyboardEvent) => {
    // Skip if user is typing in an input, textarea, or contenteditable
    const target = event.target as HTMLElement;
    if (
      target.tagName === 'INPUT' ||
      target.tagName === 'TEXTAREA' ||
      target.contentEditable === 'true' ||
      target.isContentEditable
    ) {
      // Allow ESC to close modals even when typing
      if (event.key === 'Escape') {
        const escapeShortcuts = shortcuts.filter(s => s.key === 'Escape');
        if (escapeShortcuts.length > 0) {
          event.preventDefault();
          escapeShortcuts[0].callback();
        }
      }
      return;
    }

    // Check each shortcut
    for (const shortcut of shortcuts) {
      const keyMatch = event.key === shortcut.key;
      const metaMatch = shortcut.metaKey ? event.metaKey : !event.metaKey;
      const ctrlMatch = shortcut.ctrlKey ? event.ctrlKey : !event.ctrlKey;
      const shiftMatch = shortcut.shiftKey ? event.shiftKey : !event.shiftKey;
      const altMatch = shortcut.altKey ? event.altKey : !event.altKey;

      if (keyMatch && metaMatch && ctrlMatch && shiftMatch && altMatch) {
        event.preventDefault();
        shortcut.callback();
        break;
      }
    }
  }, [shortcuts]);

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);
}

/**
 * Hook for tab navigation shortcuts (Cmd/Ctrl+1/2/3/4)
 * onTabChange is synchronous; navigation is immediate (Notes flush is fire-and-forget).
 */
export function useTabNavigationShortcuts(
  onTabChange: (tab: 'updates' | 'notes' | 'spend' | 'promo-codes') => void | Promise<void>
) {
  const shortcuts: KeyboardShortcut[] = [
    {
      key: '1',
      metaKey: true,
      callback: async () => {
        const r = onTabChange('updates');
        if (r && typeof (r as Promise<void>).then === 'function') await (r as Promise<void>);
      },
      description: 'Go to Updates tab'
    },
    {
      key: '2',
      metaKey: true,
      callback: async () => {
        const r = onTabChange('notes');
        if (r && typeof (r as Promise<void>).then === 'function') await (r as Promise<void>);
      },
      description: 'Go to Notes tab'
    },
    {
      key: '3',
      metaKey: true,
      callback: async () => {
        const r = onTabChange('spend');
        if (r && typeof (r as Promise<void>).then === 'function') await (r as Promise<void>);
      },
      description: 'Go to Spend tab'
    },
    {
      key: '4',
      metaKey: true,
      callback: async () => {
        const r = onTabChange('promo-codes');
        if (r && typeof (r as Promise<void>).then === 'function') await (r as Promise<void>);
      },
      description: 'Go to Promo Codes tab'
    }
  ];

  useKeyboardShortcuts(shortcuts);
}

